<script>
import DateEditor from '@/components/widgets/popovers/date-editor.vue';
import QuantityEditor from '@/components/widgets/popovers/quantity-editor.vue';
import Repositories from '@/repositories/no-authorization';
import dateMixin from '@/mixins/dateMixin';
import 'vue-loading-overlay/dist/vue-loading.css';
import Loading from 'vue-loading-overlay';
import MultipleItems from '@/components/widgets/popovers/multiple-items';

export default {
  props: {
    orderIdPublic: String,
  },
  components: {
    DateEditor,
    Loading,
    QuantityEditor,
    MultipleItems,
  },
  mixins: [dateMixin],
  data() {
    return {
      orderItemsList: [],
      isLoading: false,
      isLoadingOrderItemIdPublic: null,
      isErrorOrderItemIdPublic: null,
      lastLoadingType: null,
      orderItemsRepository: null,
      singleConfigsRepository: null,
      splitItemsByWorkOrder: false,
      currencyFormatter: null,
      currency: 'USD',
    };
  },
  created() {
    this.orderItemsRepository = Repositories.get(
      'orderitems',
      this.$route.params.orgcode
    );

    this.singleConfigsRepository = Repositories.get(
      'singleconfigs',
      this.$route.params.orgcode
    );

    this.loadData();
  },
  methods: {
    loadData() {
      this.isLoading = true;
      let promise1 = this.orderItemsRepository.actions
        .getByOrderWithWorkOrderItemOrderItems(this.orderIdPublic)
        .then(({ data }) => {
          this.orderItemsList = data;
        });

      let promise2 = this.singleConfigsRepository.actions
        .getSplitItemsByWorkOrder(this.orderIdPublic)
        .then(({ data }) => {
          this.splitItemsByWorkOrder = data;
        });

      let promise3 = this.singleConfigsRepository.actions
        .getCurrency()
        .then(({ data }) => {
          this.currency = data;

          this.reloadCurrencyFormatter();
        });

      Promise.all([promise1, promise2, promise3])
        .catch(() => {
          this.$toast.errorToast(this.$t('errors.errorLoading'));
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    reloadCurrencyFormatter() {
      // Helper function to create a custom currency formatter
      const createCurrencyFormatter = (locale, currency) => {
        // Create number formatter without currency style
        const numberFormatter = new Intl.NumberFormat(locale, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 5,
        });

        // Create currency formatter to get currency symbol
        const currencyFormatter = new Intl.NumberFormat(locale, {
          style: 'currency',
          currency: currency,
          currencyDisplay: 'narrowSymbol',
        });

        return {
          format: (value) => {
            // Get the currency symbol
            const formatted = currencyFormatter.format(0);
            const symbol = formatted.replace(/[\d.,\s]/g, '');

            // Format the number and combine with symbol
            const formattedNumber = numberFormatter.format(value);

            // Check if symbol comes before or after (locale dependent)
            const symbolFirst = formatted.indexOf(symbol) === 0;

            return symbolFirst
              ? `${symbol}${formattedNumber}`
              : `${formattedNumber}${symbol}`;
          },
        };
      };

      this.currencyFormatter = createCurrencyFormatter(
        this.$i18n.locale,
        this.currency
      );
    },
    async setEditedDate(newDate, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.idPublic;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'date';

        let payload = { ...orderItem, receptionDate: newDate };

        if ((await this.updateOrderItem(payload)) == false) {
          this.isErrorOrderItemIdPublic = orderItem.idPublic;
        }
      }
    },
    async setEditedPrice(newPrice, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.idPublic;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'price';

        let payload = {
          ...orderItem,
          externalEditedPrice: Number(newPrice).toFixed(5),
          isPriceEditedExternal: true,
        };

        if (payload.externalEditedPrice < 0) {
          payload.externalEditedPrice = 0;
        }

        if ((await this.updateOrderItem(payload)) == false) {
          this.isErrorOrderItemIdPublic = orderItem.idPublic;
        }
      }
    },
    async setEditedBOQuantity(boQuantity, orderItem) {
      if (this.isLoadingOrderItemIdPublic == null) {
        this.isLoadingOrderItemIdPublic = orderItem.idPublic;
        this.isErrorOrderItemIdPublic = null;
        this.lastLoadingType = 'bo';

        let payload = {
          ...orderItem,
          boQuantity: +boQuantity,
        };

        if (payload.boQuantity < 0) {
          payload.boQuantity = 0;
        }

        if (payload.boQuantity > payload.totalQuantity) {
          payload.boQuantity = payload.totalQuantity;
        }

        if ((await this.updateOrderItem(payload)) == false) {
          this.isErrorOrderItemIdPublic = orderItem.idPublic;
        }
      }
    },
    async updateOrderItem(payload) {
      try {
        await this.orderItemsRepository.update(payload, payload.idPublic);

        let foundIndex = this.orderItemsList.findIndex(
          (x) => x.idPublic == payload.idPublic
        );

        if (foundIndex >= 0) {
          let newOrderItem = {
            ...this.orderItemsList[foundIndex],
            receptionDate: payload.receptionDate,
            externalEditedPrice: payload.externalEditedPrice,
            isPriceEditedExternal: payload.isPriceEditedExternal,
            boQuantity: payload.boQuantity,
          };

          this.orderItemsList = [
            ...this.orderItemsList.slice(0, foundIndex),
            newOrderItem,
            ...this.orderItemsList.slice(foundIndex + 1),
          ];
        }

        return true;
      } catch (error) {
        this.$toast.errorToast(this.$t('errors.errorLoading'));
        return false;
      } finally {
        this.isLoadingOrderItemIdPublic = null;
      }
    },
  },
  computed: {
    orderItemsListComputed() {
      if (this.orderItemsList == null) return [];

      return this.orderItemsList.flatMap((x) => {
        let currentPrice = +Number(x.itemSupplierPrice).toFixed(5);

        if (x.isPriceEditedExternal) {
          currentPrice = +Number(x.externalEditedPrice).toFixed(5);
        } else if (x.isPriceEdited) {
          currentPrice = +Number(x.editedPrice).toFixed(5);
        } else if (x.itemSupplierPriceThreshold != null) {
          const sortedThresholds = x.itemSupplierPriceThreshold
            .filter((threshold) => !threshold.isDeleted) // Only consider active thresholds
            .sort((a, b) => b.minimum - a.minimum);

          const applicableThreshold = sortedThresholds.find(
            (threshold) => x.totalUnits >= threshold.minimum
          );

          if (applicableThreshold) {
            currentPrice = +Number(applicableThreshold.price).toFixed(5);
          }
        }

        let returnValue = {
          ...x,
          subItem: false,
          price: currentPrice,
          arrayKey: x.idPublic,
          workOrderNames: [],
        };

        if (x.editedItemCode) {
          returnValue.gridItemCode = x.editedItemCode;
        } else if (x.itemSupplierCode) {
          returnValue.gridItemCode = x.itemSupplierCode;
        } else {
          returnValue.gridItemCode = x.itemCode;
        }

        if (x.editedItemName) {
          returnValue.gridItemName = x.editedItemName;
        } else if (x.itemSupplierName) {
          returnValue.gridItemName = x.itemSupplierName;
        } else {
          returnValue.gridItemName = x.itemName;
        }

        if (x.workOrderItemOrderItems == null) {
          return returnValue;
        } else if (
          this.splitItemsByWorkOrder &&
          x.workOrderItemOrderItems.length > 1 &&
          x.totalQuantity == x.totalUnits
        ) {
          let returnArray = [];
          let totalUnits = x.totalUnits;
          let totalQuantity = x.totalQuantity;
          let reservedQuantity = x.reservedQuantity;
          let receivedQuantity = x.receivedQuantity;
          let receivedReservedQuantity = x.receivedReservedQuantity;

          for (let woioi of x.workOrderItemOrderItems) {
            totalUnits -= woioi.totalQuantity;
            totalQuantity -= woioi.totalQuantity;
            reservedQuantity -= woioi.totalQuantity;
            receivedQuantity -= woioi.reservedQuantity;
            receivedReservedQuantity -= woioi.reservedQuantity;

            returnArray.push({
              ...x,
              subItem: true,
              arrayKey: x.idPublic + '-' + woioi.idPublic,
              price: currentPrice,
              totalUnits: woioi.totalQuantity,
              totalQuantity: woioi.totalQuantity,
              reservedQuantity: woioi.totalQuantity,
              receivedQuantity: woioi.reservedQuantity,
              receivedReservedQuantity: woioi.reservedQuantity,
              workOrderNames: [woioi.workOrderName],
            });
          }

          returnValue.totalUnits = totalUnits;
          returnValue.totalQuantity = totalQuantity;
          returnValue.reservedQuantity = reservedQuantity;
          returnValue.receivedQuantity = receivedQuantity;
          returnValue.receivedReservedQuantity = receivedReservedQuantity;
          returnArray.unshift(returnValue);

          return returnArray;
        } else {
          returnValue.workOrderNames = [
            ...new Set(
              x.workOrderItemOrderItems.map((item) => item.workOrderName)
            ),
          ];

          return returnValue;
        }
      });
    },
  },
  watch: {
    '$i18n.locale': function() {
      this.reloadCurrencyFormatter();
    },
  },
};
</script>

<template>
  <div>
    <loading
      :active.sync="isLoading"
      :can-cancel="false"
      color="#f1b44c"
      :width="70"
      :height="70"
      loader="dots"
      :is-full-page="false"
    >
    </loading>
    <b-row class="custom-grid-header mt-2">
      <b-col cols="1" class="custom-grid-header-column">{{
        $t('general.code')
      }}</b-col>
      <b-col cols="2" class="custom-grid-header-column">{{
        $t('items.item')
      }}</b-col>
      <b-col cols="2" class="custom-grid-header-column text-center">{{
        $t('items.receptionDate')
      }}</b-col>
      <b-col cols="2" class="custom-grid-header-column text-center">{{
        $t('workOrders.workOrders')
      }}</b-col>
      <b-col cols="2" class="custom-grid-header-column text-center">{{
        $t('items.price')
      }}</b-col>
      <b-col cols="2" class="custom-grid-header-column text-center">{{
        $t('items.total')
      }}</b-col>
      <b-col cols="1" class="custom-grid-header-column text-center">{{
        $t('items.bo')
      }}</b-col>
    </b-row>

    <b-row
      v-for="orderItem in orderItemsListComputed"
      :key="orderItem.arrayKey"
      class="custom-grid-row"
    >
      <b-col cols="1" class="custom-grid-row-column">
        {{ orderItem.gridItemCode }}
      </b-col>
      <b-col cols="2" class="custom-grid-row-column">
        <div>
          {{ orderItem.gridItemName }}
          <span
            v-if="
              orderItem.isInternalOrderItemNote === false &&
                orderItem.orderItemNote != null &&
                orderItem.orderItemNote.length > 0
            "
          >
            <b-popover
              :target="'popover-orderitem-' + orderItem.arrayKey"
              triggers="hover"
              placement="top"
              variant="warning"
            >
              {{ orderItem.orderItemNote }}
            </b-popover>
            <i
              :id="'popover-orderitem-' + orderItem.arrayKey"
              style="font-size: 1.2em; cursor: pointer"
              class="fas fa-sticky-note text-warning"
            ></i>
          </span>
        </div>
      </b-col>
      <b-col
        cols="2"
        :id="'popover-edit-date-' + orderItem.arrayKey"
        class="custom-grid-row-column text-center"
      >
        <template v-if="!orderItem.subItem">
          <div v-if="isLoadingOrderItemIdPublic == orderItem.arrayKey">
            <i
              class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div
            v-else-if="
              isErrorOrderItemIdPublic == orderItem.arrayKey &&
                lastLoadingType == 'date'
            "
          >
            <i
              class="fas fa-exclamation text-danger ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div
            v-else-if="orderItem.receptionDate != null"
            class="clickable-text"
          >
            {{ formatDate(orderItem.receptionDate) }}
          </div>
          <div
            v-else-if="orderItem.isOrderOrdered == false"
            style="font-size: 1.3em; cursor: pointer"
          >
            <i class="fas fa-plus text-success ml-2 mr-2"></i>
          </div>
        </template>
      </b-col>
      <b-col cols="2" class="custom-grid-row-column text-center">
        <MultipleItems
          :list="orderItem.workOrderNames"
          :uniqueId="orderItem.arrayKey"
        />
      </b-col>
      <b-col
        cols="2"
        :id="'popover-edit-price-' + orderItem.arrayKey"
        class="custom-grid-row-column text-center"
      >
        <template v-if="!orderItem.subItem">
          <div v-if="isLoadingOrderItemIdPublic == orderItem.arrayKey">
            <i
              class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div
            v-else-if="
              isErrorOrderItemIdPublic == orderItem.arrayKey &&
                lastLoadingType == 'price'
            "
          >
            <i
              class="fas fa-exclamation text-danger ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div v-else-if="orderItem.isOrderOrdered">
            <span v-if="currencyFormatter != null">
              {{ currencyFormatter.format(orderItem.price) }}
            </span>
          </div>
          <div
            v-else
            :class="
              orderItem.isPriceEditedExternal
                ? 'text-warning clickable-text'
                : 'clickable-text'
            "
          >
            <span v-if="currencyFormatter != null">
              {{ currencyFormatter.format(orderItem.price) }}
            </span>
          </div>
        </template>
      </b-col>
      <b-col cols="2" class="custom-grid-row-column text-center">
        {{ orderItem.totalUnits }}
        {{ orderItem.itemSupplierUnitName }}
        <template
          v-if="
            orderItem.itemSupplierOrderUnitQuantity != 1 &&
              orderItem.itemSupplierOrderUnitQuantity != 0
          "
        >
          {{ $t('general.of') }}
          {{ orderItem.itemSupplierOrderUnitQuantity }}
        </template>
      </b-col>
      <b-col
        cols="1"
        :id="'popover-edit-bo-quantity-' + orderItem.arrayKey"
        class="custom-grid-row-column text-center"
      >
        <template v-if="!orderItem.subItem">
          <div v-if="isLoadingOrderItemIdPublic == orderItem.arrayKey">
            <i
              class="fas fa-spinner fa-spin text-warning ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div
            v-else-if="
              isErrorOrderItemIdPublic == orderItem.arrayKey &&
                lastLoadingType == 'bo'
            "
          >
            <i
              class="fas fa-exclamation text-danger ml-2 mr-2"
              style="font-size: 1.3em"
            ></i>
          </div>
          <div v-else-if="orderItem.isOrderOrdered">
            {{ orderItem.boQuantity }}
          </div>
          <div
            v-else
            :class="
              orderItem.boQuantity > 0
                ? 'text-warning clickable-text'
                : ' clickable-text'
            "
          >
            {{ orderItem.boQuantity }}
          </div>
        </template>
      </b-col>

      <DateEditor
        v-if="orderItem.isOrderOrdered == false"
        :returnedObject="orderItem"
        :target="'popover-edit-date-' + orderItem.arrayKey"
        :defaultValue="orderItem.receptionDate"
        @set-edited-date="setEditedDate"
      />

      <QuantityEditor
        v-if="orderItem.isOrderOrdered == false"
        :target="'popover-edit-price-' + orderItem.arrayKey"
        :minQuantity="+0"
        precision="'.01'"
        placement="top"
        :returnedObject="orderItem"
        :defaultQuantity="orderItem.price"
        :title="$t('general.editPrice')"
        @set-edited-quantity="setEditedPrice"
      />

      <QuantityEditor
        v-if="orderItem.isOrderOrdered == false"
        :target="'popover-edit-bo-quantity-' + orderItem.arrayKey"
        :minQuantity="+0"
        :maxQuantity="+orderItem.totalQuantity"
        placement="top"
        :returnedObject="orderItem"
        :defaultQuantity="orderItem.boQuantity"
        :title="$t('general.editQuantity')"
        @set-edited-quantity="setEditedBOQuantity"
      />

      <b-popover
        v-if="
          isErrorOrderItemIdPublic == orderItem.arrayKey &&
            lastLoadingType == 'date'
        "
        show
        :target="'popover-edit-date-' + orderItem.arrayKey"
        triggers="hover"
        placement="bottom"
        variant="danger"
      >
        <div style="font-size: 1.2em">
          {{ $t('errors.operationFailedTryAgain') }}
        </div>
      </b-popover>
      <b-popover
        v-if="
          isErrorOrderItemIdPublic == orderItem.arrayKey &&
            lastLoadingType == 'price'
        "
        show
        :target="'popover-edit-price-' + orderItem.arrayKey"
        triggers="hover"
        placement="bottom"
        variant="danger"
      >
        <div style="font-size: 1.2em">
          {{ $t('errors.operationFailedTryAgain') }}
        </div>
      </b-popover>
      <b-popover
        v-if="
          isErrorOrderItemIdPublic == orderItem.arrayKey &&
            lastLoadingType == 'bo'
        "
        show
        :target="'popover-edit-bo-quantity-' + orderItem.arrayKey"
        triggers="hover"
        placement="bottom"
        variant="danger"
      >
        <div style="font-size: 1.2em">
          {{ $t('errors.operationFailedTryAgain') }}
        </div>
      </b-popover>
    </b-row>
  </div>
</template>

<style scoped></style>
